import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../../../../environments/environment';
import { ApiMethodService } from '../../../../tools/services/api/method-api/apiMethod.service';
import { ActionBackOfficeResponse } from '../../../models/api.model';
import { ClosingTransaction, ParkingCardInfoResponse } from '../../../models/parking.model';
import { PatchCorrectLicensePayload } from '../../../models/vehicle.model';
import {
  ForceEnterTransactionPayload,
  ForceExitTransactionPayload,
  ParkingTransactionChartDto,
  ParkingTransactionChartQuery,
  ParkingTransactionTableDto,
  ParkingTransactionTableQuery,
  VehiclesInParkingTableQuery,
} from '../../../models/parking/transaction-parking.model';

@Injectable({
  providedIn: 'root',
})
export class ApiTransactionParkingService {
  private readonly baseUrl = environment.apiPrivateUrl;
  private readonly cookieService = inject(CookieService);
  private readonly apiMethodService = inject(ApiMethodService);

  getParkingTransactionForTable(query: ParkingTransactionTableQuery): Observable<ParkingTransactionTableDto> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = {
      siteId: query.siteId,
      userId: query.userId,
      startDate: query.startDate,
      endDate: query.endDate,
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 20,
    };

    if (query.searchField) params['searchField'] = query.searchField;
    if (query.searchText) params['searchText'] = query.searchText;
    if (query.sortColumn) params['sortColumn'] = query.sortColumn;
    if (query.sortDirection) params['sortDirection'] = query.sortDirection;
    // Always send status so primary filter reloads server-side every time (including 'all').
    params['status'] = query.status ?? 'all';
    if (query.gateNames) params['gateNames'] = query.gateNames;
    if (query.vehicleTypeNames) params['vehicleTypeNames'] = query.vehicleTypeNames;

    return this.apiMethodService.request<ParkingTransactionTableDto>(
      '/backoffice/getParkingTransactionForTable',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getParkingTransactionForTable',
        token,
        headerOnly: false,
        params,
      }
    );
  }

  getParkingTransactionChart(query: ParkingTransactionChartQuery): Observable<ParkingTransactionChartDto> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ParkingTransactionChartDto>(
      '/backoffice/getParkingTransactionChart',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: false,
        labelLog: 'getParkingTransactionChart',
        token,
        headerOnly: false,
        params: {
          siteId: query.siteId,
          userId: query.userId,
          startDate: query.startDate,
          endDate: query.endDate,
        },
      }
    );
  }

  patchCorrectLicense(body: PatchCorrectLicensePayload, transactionId: number): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      `/backoffice/ChangeLicenseTransaction/${transactionId}`,
      'PATCH',
      this.baseUrl,
      {
        isConsoleLog: false,
        labelLog: 'ChangeLicenseTransaction',
        token,
        headerOnly: false,
        body,
      }
    );
  }

  closingTransaction(body: ClosingTransaction, transactionId: number): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      `/backoffice/ClosingTransaction/${transactionId}`,
      'PATCH',
      this.baseUrl,
      {
        isConsoleLog: false,
        labelLog: 'ClosingTransaction',
        token,
        headerOnly: false,
        body,
      }
    );
  }

  getParkingCardInfoByCardCode(cardCode: string): Observable<ParkingCardInfoResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ParkingCardInfoResponse>(
      '/backoffice/GetParkingCardInfoByCardCode',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'GetParkingCardInfoByCardCode',
        token,
        headerOnly: false,
        params: { cardCode },
      }
    );
  }

  /** บัตรสมาชิกจากช่อง บัตร/สมาชิก (cardName) + site ของธุรกรรม */
  getParkingCardInfoByCardName(siteId: number, cardName: string): Observable<ParkingCardInfoResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ParkingCardInfoResponse>(
      '/backoffice/GetParkingCardInfoByCardName',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'GetParkingCardInfoByCardName',
        token,
        headerOnly: false,
        params: { siteId, cardName },
      }
    );
  }

  forceEnterTransaction(body: ForceEnterTransactionPayload): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/backoffice/forceEnterTransaction',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'forceEnterTransaction',
        token,
        headerOnly: false,
        body,
      }
    );
  }

  forceExitTransaction(body: ForceExitTransactionPayload): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/backoffice/forceExitTransaction',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'forceExitTransaction',
        token,
        headerOnly: false,
        body,
      }
    );
  }

  getVehiclesInParkingForTable(query: VehiclesInParkingTableQuery): Observable<ParkingTransactionTableDto> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = {
      siteId: query.siteId,
      userId: query.userId,
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 20,
    };

    if (query.searchField) params['searchField'] = query.searchField;
    if (query.searchText) params['searchText'] = query.searchText;
    if (query.sortColumn) params['sortColumn'] = query.sortColumn;
    if (query.sortDirection) params['sortDirection'] = query.sortDirection;
    if (query.gateNames) params['gateNames'] = query.gateNames;
    if (query.vehicleTypeNames) params['vehicleTypeNames'] = query.vehicleTypeNames;

    return this.apiMethodService.request<ParkingTransactionTableDto>(
      '/backoffice/getVehiclesInParkingForTable',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getVehiclesInParkingForTable',
        token,
        headerOnly: false,
        params,
      }
    );
  }
}
